import React, { useState, useContext, useEffect } from "react";
import { Form, Button, Card, Alert, Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../api"; // Import the configured API instance
import AuthContext from "../contexts/AuthContext";

const Register = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);

  // Redirect if already authenticated
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate("/");
    }
  }, [auth.isAuthenticated, navigate]);

  const { username, email, password, confirmPassword, firstName, lastName } =
    formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // Register the user - using configured API instance with proper base URL
      await api.post("/api/users/register/", {
        username,
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });

      setSuccess("Registration successful! You can now login.");

      // Reset form
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Registration error:", err);
      if (err.response?.data) {
        // Format Django validation errors
        const errData = err.response.data;
        const errMessages = [];
        for (const field in errData) {
          // Check if the field error is an array or string
          if (Array.isArray(errData[field])) {
            errMessages.push(`${field}: ${errData[field].join(" ")}`);
          } else if (typeof errData[field] === "string") {
            errMessages.push(`${field}: ${errData[field]}`);
          } else if (typeof errData[field] === "object") {
            errMessages.push(`${field}: ${JSON.stringify(errData[field])}`);
          } else {
            errMessages.push(`${field}: Invalid format`);
          }
        }
        setError(errMessages.join("\n"));
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // If still loading, show loading indicator
  if (auth.loading) {
    return (
      <Container className="text-center mt-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "70vh" }}
    >
      <Card className="w-100" style={{ maxWidth: "500px" }}>
        <Card.Header as="h4" className="text-center">
          Register for CSV CRUD App
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>
              {error}
            </Alert>
          )}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="username">
              <Form.Label>Username*</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={username}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={email}
                onChange={handleChange}
                required
              />
              <Form.Text className="text-muted">
                Each email address can only be used once. If you already have an
                account with this email, please log in.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                value={firstName}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                value={lastName}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password*</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={password}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="confirmPassword">
              <Form.Label>Confirm Password*</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <div className="d-grid">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? "Registering..." : "Register"}
              </Button>
            </div>
          </Form>
        </Card.Body>
        <Card.Footer className="text-center">
          <div className="mb-2">
            Already have an account?{" "}
            <Button
              variant="link"
              onClick={() => navigate("/login")}
              className="p-0"
            >
              Login
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default Register;
